// Project info on the property detail page. When a unit is linked to a project,
// this card shows the project's shared fields (handover year, floors, basements,
// service + parking fees) and links to the project's other units. Renders
// nothing for an unlinked unit. The fields come from the project — one source
// of truth — so they read the same across every unit in the building.

import { Link } from "react-router";
import { ArrowRight, Building2 } from "lucide-react";

import { cn } from "@/lib/utils";
import { useProject } from "@/features/project/api";
import type { Property } from "@/types/property";

/** "1.200.000 đ" — plain VND, grouped, for the small fee figures. */
function formatVnd(value: number): string {
  return `${new Intl.NumberFormat("vi-VN").format(value)} đ`;
}

export function ProjectInfoCard({
  property,
  className,
}: {
  property: Property;
  className?: string;
}) {
  const projectId = property.projectId;
  const projectQuery = useProject(projectId ?? undefined);
  const project = projectQuery.data;

  if (projectId === null || project === undefined) return null;

  const rows: Array<{ label: string; value: string }> = [];
  if (project.completionYear != null)
    rows.push({ label: "Năm bàn giao", value: String(project.completionYear) });
  if (project.totalFloors != null)
    rows.push({ label: "Tổng số tầng", value: `${project.totalFloors} tầng` });
  if (project.basements != null)
    rows.push({ label: "Số tầng hầm", value: `${project.basements} hầm` });
  if (project.serviceFee != null)
    rows.push({ label: "Phí dịch vụ", value: formatVnd(project.serviceFee) });
  if (project.parkingFeeCar != null)
    rows.push({ label: "Phí gửi ô tô", value: formatVnd(project.parkingFeeCar) });
  if (project.parkingFeeMotorbike != null)
    rows.push({
      label: "Phí gửi xe máy",
      value: formatVnd(project.parkingFeeMotorbike),
    });

  return (
    <section
      className={cn(
        "rounded-xl border border-border bg-card p-4 shadow-[var(--shadow-soft)]",
        className,
      )}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Building2 aria-hidden="true" className="size-4" />
          </span>
          <div className="min-w-0">
            <div className="font-mono text-[11px] tracking-wider text-ink-faint uppercase">
              Dự án
            </div>
            <div className="truncate text-[14px] font-semibold">{project.name}</div>
          </div>
        </div>
        <Link
          to={`/properties?projectId=${project.id}`}
          className="inline-flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-[12px] font-medium text-primary hover:bg-primary/10"
        >
          {project.unitCount} căn
          <ArrowRight aria-hidden="true" className="size-3.5" />
        </Link>
      </div>

      {rows.length > 0 ? (
        <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2">
          {rows.map((r) => (
            <div key={r.label} className="flex flex-col">
              <dt className="text-[11px] text-muted-foreground">{r.label}</dt>
              <dd className="text-[14px] font-medium">{r.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
    </section>
  );
}
